import React, { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Drawer,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Divider,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { useTheme } from '@mui/material/styles';
import FilterListIcon from '@mui/icons-material/FilterList';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const SpotSection = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { totalBalances, spotBalances, coinPrices, error } = useSelector((state) => state.assets);

  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [hideZeroBalances, setHideZeroBalances] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [minBalance, setMinBalance] = useState(0);
  const [pricesLoaded, setPricesLoaded] = useState(false);
  const [hideBalances, setHideBalances] = useState(false);
  const [pieDrawerOpen, setPieDrawerOpen] = useState(false);
  const user = useSelector((state) => state.user.data);
  // Initialize prices for non-zero balances and set 0.0 for zero balances
  useEffect(() => {
    const symbols = spotBalances
      .filter(balance => balance.balance > 0)
      .map(balance => balance.asset_id?.symbol?.toLowerCase())
      .filter(symbol => symbol && symbol !== 'usdt');

    console.log('Symbols to fetch prices for:', symbols);

    // Ensure zero-balance assets have a price of 0.0 if not already set
    spotBalances.forEach(balance => {
      const symbol = balance.asset_id?.symbol?.toLowerCase();
      if (balance.balance === 0 && (coinPrices[symbol] === undefined || coinPrices[symbol] === null)) {
        // Price update will be handled by AssetsPage.js WebSocket
      }
    });

    if (symbols.length === 0) {
      setPricesLoaded(true);
      return;
    }

    const allPricesLoaded = symbols.every(symbol => coinPrices[symbol] !== undefined && coinPrices[symbol] !== null);
    if (allPricesLoaded) {
      setPricesLoaded(true);
    } else {
      const timer = setTimeout(() => {
        console.warn('Price loading timed out. Proceeding with available data.');
        setPricesLoaded(true);
      }, 5000); // Reduced timeout to 5 seconds
      return () => clearTimeout(timer);
    }
  }, [spotBalances, coinPrices]);

  const toggleFilterDrawer = () => {
    setFilterDrawerOpen(!filterDrawerOpen);
  };

  const toggleHideBalances = () => {
    setHideBalances(!hideBalances);
  };

  const togglePieDrawer = () => {
    setPieDrawerOpen(!pieDrawerOpen);
  };

  // Filter balances based on filters
  const filteredBalances = useMemo(() => {
    let filtered = spotBalances;

    if (hideZeroBalances) {
      filtered = filtered.filter(balance => balance.balance > 0);
    }

    if (filterType !== 'all') {
      const stableCoins = ['USDT', 'USDC', 'BUSD'];
      const majorCoins = ['BTC', 'ETH', 'BNB'];
      if (filterType === 'stablecoins') {
        filtered = filtered.filter(balance => stableCoins.includes(balance.asset_id?.symbol));
      } else if (filterType === 'major') {
        filtered = filtered.filter(balance => majorCoins.includes(balance.asset_id?.symbol));
      } else if (filterType === 'altcoins') {
        filtered = filtered.filter(
          balance => !stableCoins.includes(balance.asset_id?.symbol) && !majorCoins.includes(balance.asset_id?.symbol)
        );
      }
    }

    if (minBalance > 0) {
      filtered = filtered.filter(balance => {
        const priceInUSD = balance.asset_id?.symbol === 'USDT'
          ? 1
          : coinPrices[balance.asset_id?.symbol?.toLowerCase()] || 0;
        const valueInUSD = balance.balance * priceInUSD;
        return valueInUSD >= minBalance;
      });
    }

    return filtered;
  }, [spotBalances, hideZeroBalances, filterType, minBalance, coinPrices]);

  // Calculate total spot balance in USDT
  const totalSpotBalanceInUSDT = useMemo(() => {
    return spotBalances.reduce((total, balance) => {
      const priceInUSD = balance.asset_id?.symbol === 'USDT'
        ? 1
        : coinPrices[balance.asset_id?.symbol?.toLowerCase()] || 0;
      const valueInUSD = balance.balance * priceInUSD;
      return total + valueInUSD;
    }, 0).toFixed(2);
  }, [spotBalances, coinPrices]);

  // Function to determine coin color based on symbol
  const getCoinColor = (symbol) => {
    switch (symbol.toUpperCase()) {
      case 'BTC':
        return '#F7931A'; // Orange for Bitcoin
      case 'ETH':
        return '#627EEA'; // Blue for Ethereum
      case 'USDT':
        return '#26A17B'; // Green for USDT
      case 'BNB':
        return '#F3BA2F'; // Yellow for BNB
      default:
        return '#8884D8'; // Default color for other coins
    }
  };

  // Calculate percentage data for each coin in Spot Wallet
  const pieData = useMemo(() => {
    return spotBalances
      .filter(balance => balance.balance > 0)
      .map((balance, index) => {
        const symbol = balance.asset_id?.symbol?.toLowerCase();
        const priceInUSD = balance.asset_id?.symbol === 'USDT'
          ? 1
          : coinPrices[symbol] || 0;
        const valueInUSD = balance.balance * priceInUSD;
        const percentage = totalSpotBalanceInUSDT > 0 ? ((valueInUSD / totalSpotBalanceInUSDT) * 100).toFixed(1) : 0;
        return {
          id: index,
          value: parseFloat(percentage),
          label: balance.asset_id?.symbol,
          color: getCoinColor(balance.asset_id?.symbol),
          valueInUSD: valueInUSD.toFixed(2),
        };
      });
  }, [spotBalances, coinPrices, totalSpotBalanceInUSDT]);

  // Function to mask balances with asterisks
  const maskBalance = (balance) => {
    const length = balance.toString().length;
    return '*'.repeat(Math.max(length, 6)); // Minimum 6 asterisks
  };

  return (
    <Box sx={{ mt: 3 }}>
      {/* Est. Assets Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
          Est. Assets
        </Typography>
        <IconButton onClick={toggleHideBalances} sx={{ ml: 1, color: theme.palette.text.secondary }}>
          {hideBalances ? <VisibilityOffIcon /> : <VisibilityIcon />}
        </IconButton>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ color: theme.palette.text.primary, fontWeight: 'bold' }}>
            {hideBalances
              ? maskBalance(totalBalances.spotBalances?.find(b => b.asset_id?.symbol === 'USDT')?.balance.toFixed(2) || '0.00')
              : (totalBalances.spotBalances?.find(b => b.asset_id?.symbol === 'USDT')?.balance.toFixed(2) || '0.00')}{' '}
            USDT
          </Typography>
          <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
            Total Balance: {hideBalances ? maskBalance(totalSpotBalanceInUSDT) : totalSpotBalanceInUSDT} USDT
          </Typography>
        </Box>
        {/* Pie chart for coin distribution */}
        {pieData.length > 0 && (
          <Box
            sx={{
              ml: 2,
              cursor: 'pointer',
              transition: 'all 0.3s',
              '&:hover': {
                transform: 'scale(1.1)',
                filter: 'drop-shadow(0 0 8px rgba(0, 0, 0, 0.2))',
              },
            }}
            onClick={togglePieDrawer}
          >
            <PieChart
              series={[
                {
                  data: pieData,
                  innerRadius: 18,
                  outerRadius: 30,
                  paddingAngle: 3,
                  cornerRadius: 5,
                  highlightScope: { faded: 'global', highlighted: 'item' },
                  faded: { innerRadius: 15, additionalRadius: -5 },
                },
              ]}
              width={80}
              height={80}
              margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
              slotProps={{
                legend: { hidden: true }, // Hide legend
              }}
            />
          </Box>
        )}
      </Box>
      {error && (
        <Typography variant="body2" sx={{ color: 'red', mt: 1 }}>
          Error: {error}
        </Typography>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Button
          variant="outlined"
          sx={{
            borderColor: theme.palette.brand.main,
            color: theme.palette.brand.main,
            flex: 1,
            mr: 1,
            borderRadius: 2,
            fontWeight: 'medium',
            transition: 'all 0.3s',
            '&:hover': {
              borderColor: theme.palette.brand.main,
              bgcolor: `${theme.palette.brand.main}20`,
              transform: 'translateY(-2px)',
              boxShadow: theme.shadows[2],
            },
          }}
          onClick={() => navigate('/deposit')}
        >
          Deposit
        </Button>
        <Button
          variant="outlined"
          sx={{
            borderColor: theme.palette.brand.main,
            color: theme.palette.brand.main,
            flex: 1,
            mx: 1,
            borderRadius: 2,
            fontWeight: 'medium',
            transition: 'all 0.3s',
            '&:hover': {
              borderColor: theme.palette.brand.main,
              bgcolor: `${theme.palette.brand.main}20`,
              transform: 'translateY(-2px)',
              boxShadow: theme.shadows[2],
            },
          }}
          onClick={() => (user && user.role === 'Support' ? navigate('/staff/withdraw-requests') : navigate('/withdraw'))}
          
        >
          Withdraw
        </Button>
        <Button
          variant="outlined"
          sx={{
            borderColor: theme.palette.brand.main,
            color: theme.palette.brand.main,
            flex: 1,
            ml: 1,
            borderRadius: 2,
            fontWeight: 'medium',
            transition: 'all 0.3s',
            '&:hover': {
              borderColor: theme.palette.brand.main,
              bgcolor: `${theme.palette.brand.main}20`,
              transform: 'translateY(-2px)',
              boxShadow: theme.shadows[2],
            },
          }}
          onClick={() => navigate('/transfer')}
        >
          Transfer
        </Button>
      </Box>
      <Box sx={{ mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
            Spot Wallet Balances
          </Typography>
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            sx={{
              borderColor: theme.palette.text.secondary,
              color: theme.palette.text.secondary,
              '&:hover': {
                borderColor: theme.palette.brand.main,
                color: theme.palette.brand.main,
              },
            }}
            onClick={toggleFilterDrawer}
          >
            Filters
          </Button>
        </Box>
        {pricesLoaded ? (
          <Box
            sx={{
              maxHeight: 400,
              overflowY: 'scroll',
              bgcolor: theme.palette.background.paper,
              borderRadius: 2,
              '&::-webkit-scrollbar': { width: '1px' },
              '&::-webkit-scrollbar-track': { bgcolor: theme.palette.background.paper },
              '&::-webkit-scrollbar-thumb': { bgcolor: theme.palette.text.secondary, borderRadius: '1px' },
              scrollbarWidth: 'thin',
              scrollbarColor: `${theme.palette.text.secondary} ${theme.palette.background.paper}`,
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: theme.palette.text.primary }}>Asset</TableCell>
                  <TableCell sx={{ color: theme.palette.text.primary }}>Balance</TableCell>
                  <TableCell sx={{ color: theme.palette.text.primary }}>Value (USDT)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredBalances.map((balance) => {
                  const symbol = balance.asset_id?.symbol?.toLowerCase();
                  const priceInUSD = balance.asset_id?.symbol === 'USDT'
                    ? 1
                    : coinPrices[symbol] || (balance.balance === 0 ? 0.0 : 0);
                  const valueInUSD = priceInUSD !== undefined && priceInUSD !== null
                    ? (balance.balance * priceInUSD).toFixed(2)
                    : 'N/A';
                  return (
                    <TableRow
                      key={balance._id}
                      sx={{
                        transition: 'all 0.3s',
                        '&:hover': {
                          bgcolor: `${theme.palette.brand.main}10`,
                          transform: 'translateX(2px)',
                        },
                      }}
                    >
                      <TableCell sx={{ color: theme.palette.text.primary }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            bgcolor: getCoinColor(balance.asset_id?.symbol),
                            borderRadius: '50%',
                            mr: 1,
                          }}
                        />
                        {balance.asset_id?.symbol} ({balance.asset_id?.name})
                      </TableCell>
                      <TableCell sx={{ color: theme.palette.text.primary }}>
                        {hideBalances ? maskBalance(balance.balance.toFixed(8)) : balance.balance.toFixed(8)}
                      </TableCell>
                      <TableCell sx={{ color: theme.palette.text.primary }}>
                        {hideBalances ? maskBalance(valueInUSD) : valueInUSD}{' '}
                        {priceInUSD !== undefined && priceInUSD !== null ? 'USDT' : ''}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <CircularProgress sx={{ color: theme.palette.brand.main }} />
            <Typography sx={{ ml: 2, color: theme.palette.text.secondary }}>
              Loading prices...
            </Typography>
          </Box>
        )}
      </Box>

      {/* Bottom Drawer for filters */}
      <Drawer
        anchor="bottom"
        open={filterDrawerOpen}
        onClose={toggleFilterDrawer}
        PaperProps={{
          sx: {
            bgcolor: theme.palette.background.paper,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            p: 3,
          },
        }}
      >
        <Typography variant="h6" sx={{ color: theme.palette.text.primary, mb: 2 }}>
          Filter Coins
        </Typography>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Coin Type</InputLabel>
          <Select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            label="Coin Type"
          >
            <MenuItem value="all">All Coins</MenuItem>
            <MenuItem value="stablecoins">Stablecoins</MenuItem>
            <MenuItem value="major">Major Coins</MenuItem>
            <MenuItem value="altcoins">Altcoins</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Minimum Balance (USDT)</InputLabel>
          <Select
            value={minBalance}
            onChange={(e) => setMinBalance(Number(e.target.value))}
            label="Minimum Balance (USDT)"
          >
            <MenuItem value={0}>No Minimum</MenuItem>
            <MenuItem value={1}>1 USDT</MenuItem>
            <MenuItem value={10}>10 USDT</MenuItem>
            <MenuItem value={50}>50 USDT</MenuItem>
            <MenuItem value={100}>100 USDT</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel
          control={
            <Checkbox
              checked={hideZeroBalances}
              onChange={(e) => setHideZeroBalances(e.target.checked)}
              color="primary"
            />
          }
          label="Hide Zero Balances"
          sx={{ color: theme.palette.text.primary, mb: 2 }}
        />
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            sx={{
              borderColor: theme.palette.text.secondary,
              color: theme.palette.text.secondary,
              flex: 1,
              mr: 1,
              borderRadius: 2,
              transition: 'all 0.3s',
              '&:hover': {
                borderColor: theme.palette.brand.main,
                color: theme.palette.brand.main,
              },
            }}
            onClick={() => {
              setFilterType('all');
              setMinBalance(0);
              setHideZeroBalances(false);
              toggleFilterDrawer();
            }}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            sx={{
              bgcolor: theme.palette.brand.main,
              color: theme.palette.text.primary,
              flex: 1,
              ml: 1,
              borderRadius: 2,
              transition: 'all 0.3s',
              '&:hover': {
                bgcolor: theme.palette.brand.main,
                transform: 'translateY(-2px)',
                boxShadow: theme.shadows[4],
              },
            }}
            onClick={toggleFilterDrawer}
          >
            Apply Filters
          </Button>
        </Box>
      </Drawer>

      {/* Drawer for pie chart details */}
      <Drawer
        anchor="bottom"
        open={pieDrawerOpen}
        onClose={togglePieDrawer}
        PaperProps={{
          sx: {
            bgcolor: theme.palette.background.paper,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            p: 3,
          },
        }}
      >
        <Typography variant="h6" sx={{ color: theme.palette.text.primary, mb: 2 }}>
          Spot Wallet Distribution
        </Typography>
        {pieData.map((coin) => (
          <Box key={coin.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: 12, height: 12, bgcolor: coin.color, borderRadius: '50%', mr: 1 }} />
              <Typography sx={{ color: theme.palette.text.secondary }}>
                {coin.label}:
              </Typography>
            </Box>
            <Typography sx={{ color: coin.color, fontWeight: 'medium' }}>
              {coin.value}% (${hideBalances ? maskBalance(coin.valueInUSD) : coin.valueInUSD} USDT)
            </Typography>
          </Box>
        ))}
        <Divider sx={{ my: 2 }} />
        <Button
          variant="contained"
          sx={{
            bgcolor: theme.palette.brand.main,
            color: theme.palette.text.primary,
            borderRadius: 2,
            transition: 'all 0.3s',
            '&:hover': {
              bgcolor: theme.palette.brand.main,
              transform: 'translateY(-2px)',
              boxShadow: theme.shadows[4],
            },
          }}
          onClick={togglePieDrawer}
        >
          Close
        </Button>
      </Drawer>
    </Box>
  );
};

export default SpotSection;